import React, { Component } from 'react';
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Alert, Spinner } from 'reactstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import { handlePhoneInput, getDigitsOnly } from '../../utils/phoneUtils';
import './apply.css';

export class Apply extends Component {
    static displayName = Apply.name;

    constructor(props) {
        super(props);
        this.state = {
            // Personal Information
            fullName: '',
            dateOfBirth: '',
            street: '',
            apartment: '',
            city: '',
            state: '',
            zipCode: '',
            phone: '',
            email: '',
            
            // Employment Preferences
            desiredEmployment: '',
            desiredLocation: '',
            desiredPay: '',
            educationLevel: '',
            
            // Legal Information
            legallyEligibleToWork: null,
            hasFelonyConviction: null,
            felonyExplanation: '',
            
            // Previous Employment 1
            employer1Name: '',
            employer1JobTitle: '',
            employer1Phone: '',
            employer1Street: '',
            employer1Apartment: '',
            employer1City: '',
            employer1State: '',
            employer1ZipCode: '',
            employer1Pay: '',
            employer1Responsibilities: '',
            employer1StartDate: '',
            employer1EndDate: '',
            employer1ReasonForLeaving: '',
            
            // Previous Employment 2
            employer2Name: '',
            employer2JobTitle: '',
            employer2Phone: '',
            employer2Street: '',
            employer2Apartment: '',
            employer2City: '',
            employer2State: '',
            employer2ZipCode: '',
            employer2Pay: '',
            employer2Responsibilities: '',
            employer2StartDate: '',
            employer2EndDate: '',
            employer2ReasonForLeaving: '',
            
            // References
            reference1Name: '',
            reference1Company: '',
            reference1Title: '',
            reference1Email: '',
            reference1Phone: '',
            reference2Name: '',
            reference2Company: '',
            reference2Title: '',
            reference2Email: '',
            reference2Phone: '',
            
            // Form state
            consentToBackgroundCheck: null,
            certifyTruthfulness: false,
            captchaToken: '',
            isSubmitting: false,
            submitMessage: '',
            submitError: false,
            validationErrors: {}
        };

        this.recaptchaRef = React.createRef();
    }

    handleInputChange = (event) => {
        const target = event.target;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        const name = target.name;

        this.setState({
            [name]: value
        });

        // Clear validation error for this field
        if (this.state.validationErrors[name]) {
            const newErrors = { ...this.state.validationErrors };
            delete newErrors[name];
            this.setState({ validationErrors: newErrors });
        }
    };

    handleCaptchaChange = (token) => {
        this.setState({ captchaToken: token });
    };

    getTodaysDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    handleZipCodeInput = (event, fieldName) => {
        const input = event.target;
        const cursorPosition = input.selectionStart;
        const oldValue = input.value;
        const newValue = event.target.value;
        
        // Handle backspace/delete operations
        const isDeleting = newValue.length < oldValue.length;
        
        // If user is deleting and cursor is on the hyphen, remove the previous digit
        if (isDeleting && cursorPosition > 0) {
            const charAtCursor = oldValue.charAt(cursorPosition);
            if (charAtCursor === '-') {
                // Remove the digit before the hyphen instead
                const digitsOnly = oldValue.replace(/\D/g, '');
                const newDigits = digitsOnly.slice(0, -1);
                let formatted = newDigits;
                
                if (newDigits.length > 5) {
                    formatted = newDigits.slice(0, 5) + '-' + newDigits.slice(5, 9);
                }
                
                this.setState({ [fieldName]: formatted });
                
                // Set cursor position after the state update
                setTimeout(() => {
                    const newCursorPos = Math.min(cursorPosition - 1, formatted.length);
                    input.setSelectionRange(newCursorPos, newCursorPos);
                }, 0);
                
                // Clear validation error for this field
                if (this.state.validationErrors[fieldName]) {
                    const newErrors = { ...this.state.validationErrors };
                    delete newErrors[fieldName];
                    this.setState({ validationErrors: newErrors });
                }
                return;
            }
        }
        
        // Normal formatting
        let value = newValue.replace(/\D/g, '');
        
        if (value.length > 5) {
            value = value.slice(0, 5) + '-' + value.slice(5, 9);
        }
        
        this.setState({ [fieldName]: value });
        
        // Adjust cursor position after formatting
        if (!isDeleting && value.length > oldValue.length) {
            setTimeout(() => {
                // Calculate where cursor should be based on number of digits typed
                const digitsBeforeCursor = newValue.substring(0, cursorPosition).replace(/\D/g, '').length;
                let newCursorPos = 0;
                let digitCount = 0;
                
                for (let i = 0; i < value.length; i++) {
                    if (/\d/.test(value[i])) {
                        digitCount++;
                        if (digitCount === digitsBeforeCursor) {
                            newCursorPos = i + 1;
                            break;
                        }
                    } else if (digitCount < digitsBeforeCursor) {
                        newCursorPos = i + 1;
                    }
                }
                
                input.setSelectionRange(newCursorPos, newCursorPos);
            }, 0);
        }
        
        // Clear validation error for this field
        if (this.state.validationErrors[fieldName]) {
            const newErrors = { ...this.state.validationErrors };
            delete newErrors[fieldName];
            this.setState({ validationErrors: newErrors });
        }
    };

    validateForm = () => {
        const errors = {};
        
        // Required fields validation
        if (!this.state.fullName.trim()) errors.fullName = 'Full name is required';
        if (!this.state.dateOfBirth) errors.dateOfBirth = 'Date of birth is required';
        if (!this.state.street.trim()) errors.street = 'Street address is required';
        if (!this.state.city.trim()) errors.city = 'City is required';
        if (!this.state.state.trim()) errors.state = 'State is required';
        if (!this.state.zipCode.trim()) errors.zipCode = 'Zip code is required';
        if (!this.state.phone.trim()) errors.phone = 'Phone number is required';
        if (!this.state.email.trim()) errors.email = 'Email is required';
        if (!this.state.desiredEmployment) errors.desiredEmployment = 'Desired employment type is required';
        if (!this.state.desiredLocation) errors.desiredLocation = 'Desired location is required';
        if (!this.state.educationLevel) errors.educationLevel = 'Education level is required';
        if (this.state.legallyEligibleToWork === null) errors.legallyEligibleToWork = 'Please specify work eligibility';
        if (this.state.hasFelonyConviction === null) errors.hasFelonyConviction = 'Please specify felony conviction status';
        if (this.state.consentToBackgroundCheck === null) errors.consentToBackgroundCheck = 'Please specify background check consent';
        if (!this.state.certifyTruthfulness) errors.certifyTruthfulness = 'You must certify the truthfulness of your application';
        if (!this.state.captchaToken) errors.captchaToken = 'Please complete the captcha';

        // Email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (this.state.email && !emailRegex.test(this.state.email)) {
            errors.email = 'Please enter a valid email address';
        }

        // Age validation (must be at least 21)
        if (this.state.dateOfBirth) {
            const birthDate = new Date(this.state.dateOfBirth);
            const today = new Date();
            const age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                if (age - 1 < 21) {
                    errors.dateOfBirth = 'Applicant must be at least 21 years old';
                }
            }
            if (age < 21) {
                errors.dateOfBirth = 'Applicant must be at least 21 years old';
            }
        }

        // Zip code validation
        const zipCodeRegex = /^\d{5}(-\d{4})?$/;
        if (this.state.zipCode && !zipCodeRegex.test(this.state.zipCode)) {
            errors.zipCode = 'Please enter a valid zip code (12345 or 12345-6789)';
        }
        if (this.state.employer1ZipCode && !zipCodeRegex.test(this.state.employer1ZipCode)) {
            errors.employer1ZipCode = 'Please enter a valid zip code (12345 or 12345-6789)';
        }
        if (this.state.employer2ZipCode && !zipCodeRegex.test(this.state.employer2ZipCode)) {
            errors.employer2ZipCode = 'Please enter a valid zip code (12345 or 12345-6789)';
        }

        // Felony explanation required if has felony conviction
        if (this.state.hasFelonyConviction === true && !this.state.felonyExplanation.trim()) {
            errors.felonyExplanation = 'Please explain your felony conviction';
        }

        this.setState({ validationErrors: errors });
        return Object.keys(errors).length === 0;
    };

    handleSubmit = async (event) => {
        event.preventDefault();

        if (!this.validateForm()) {
            return;
        }

        this.setState({ isSubmitting: true, submitMessage: '', submitError: false });

        const applicationData = {
            fullName: this.state.fullName,
            dateOfBirth: this.state.dateOfBirth,
            street: this.state.street,
            apartment: this.state.apartment || null,
            city: this.state.city,
            state: this.state.state,
            zipCode: this.state.zipCode,
            phone: getDigitsOnly(this.state.phone),
            email: this.state.email,
            desiredEmployment: this.state.desiredEmployment,
            desiredLocation: this.state.desiredLocation,
            desiredPay: this.state.desiredPay || null,
            legallyEligibleToWork: this.state.legallyEligibleToWork,
            hasFelonyConviction: this.state.hasFelonyConviction,
            felonyExplanation: this.state.felonyExplanation || null,
            educationLevel: this.state.educationLevel,
            
            // Employment history
            employer1Name: this.state.employer1Name || null,
            employer1JobTitle: this.state.employer1JobTitle || null,
            employer1Phone: this.state.employer1Phone ? getDigitsOnly(this.state.employer1Phone) : null,
            employer1Street: this.state.employer1Street || null,
            employer1Apartment: this.state.employer1Apartment || null,
            employer1City: this.state.employer1City || null,
            employer1State: this.state.employer1State || null,
            employer1ZipCode: this.state.employer1ZipCode || null,
            employer1Pay: this.state.employer1Pay || null,
            employer1Responsibilities: this.state.employer1Responsibilities || null,
            employer1StartDate: this.state.employer1StartDate || null,
            employer1EndDate: this.state.employer1EndDate || null,
            employer1ReasonForLeaving: this.state.employer1ReasonForLeaving || null,
            
            employer2Name: this.state.employer2Name || null,
            employer2JobTitle: this.state.employer2JobTitle || null,
            employer2Phone: this.state.employer2Phone ? getDigitsOnly(this.state.employer2Phone) : null,
            employer2Street: this.state.employer2Street || null,
            employer2Apartment: this.state.employer2Apartment || null,
            employer2City: this.state.employer2City || null,
            employer2State: this.state.employer2State || null,
            employer2ZipCode: this.state.employer2ZipCode || null,
            employer2Pay: this.state.employer2Pay || null,
            employer2Responsibilities: this.state.employer2Responsibilities || null,
            employer2StartDate: this.state.employer2StartDate || null,
            employer2EndDate: this.state.employer2EndDate || null,
            employer2ReasonForLeaving: this.state.employer2ReasonForLeaving || null,
            
            // References
            reference1Name: this.state.reference1Name || null,
            reference1Company: this.state.reference1Company || null,
            reference1Title: this.state.reference1Title || null,
            reference1Email: this.state.reference1Email || null,
            reference1Phone: this.state.reference1Phone ? getDigitsOnly(this.state.reference1Phone) : null,
            reference2Name: this.state.reference2Name || null,
            reference2Company: this.state.reference2Company || null,
            reference2Title: this.state.reference2Title || null,
            reference2Email: this.state.reference2Email || null,
            reference2Phone: this.state.reference2Phone ? getDigitsOnly(this.state.reference2Phone) : null,
            
            consentToBackgroundCheck: this.state.consentToBackgroundCheck,
            certifyTruthfulness: this.state.certifyTruthfulness,
            captchaToken: this.state.captchaToken
        };

        try {
            const response = await fetch('/api/application', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(applicationData)
            });

            const result = await response.json();

            if (response.ok && result.success) {
                this.setState({
                    submitMessage: result.message,
                    submitError: false,
                    isSubmitting: false,
                    // Reset form
                    fullName: '', dateOfBirth: '', street: '', apartment: '', city: '', state: '', zipCode: '', phone: '', email: '',
                    desiredEmployment: '', desiredLocation: '', desiredPay: '', educationLevel: '',
                    legallyEligibleToWork: null, hasFelonyConviction: null, felonyExplanation: '',
                    employer1Name: '', employer1JobTitle: '', employer1Phone: '', employer1Street: '', employer1Apartment: '', employer1City: '', employer1State: '', employer1ZipCode: '',
                    employer1Pay: '', employer1Responsibilities: '', employer1StartDate: '', employer1EndDate: '', employer1ReasonForLeaving: '',
                    employer2Name: '', employer2JobTitle: '', employer2Phone: '', employer2Street: '', employer2Apartment: '', employer2City: '', employer2State: '', employer2ZipCode: '',
                    employer2Pay: '', employer2Responsibilities: '', employer2StartDate: '', employer2EndDate: '', employer2ReasonForLeaving: '',
                    reference1Name: '', reference1Company: '', reference1Title: '', reference1Email: '', reference1Phone: '',
                    reference2Name: '', reference2Company: '', reference2Title: '', reference2Email: '', reference2Phone: '',
                    consentToBackgroundCheck: null, certifyTruthfulness: false, captchaToken: ''
                });
                // Reset reCAPTCHA
                if (this.recaptchaRef.current) {
                    this.recaptchaRef.current.reset();
                }
            } else {
                this.setState({
                    submitMessage: result.message || 'An error occurred while submitting your application.',
                    submitError: true,
                    isSubmitting: false
                });
            }
        } catch (error) {
            console.error('Application submission error:', error);
            this.setState({
                submitMessage: 'Network error. Please check your connection and try again.',
                submitError: true,
                isSubmitting: false
            });
        }
    };

    render() {
        const { validationErrors } = this.state;
        
        return (
            <div className="apply-page">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg="8">
                            <div className="apply-header text-center">
                                <h1 className="apply-title">Job Application</h1>
                                <p className="apply-subtitle">
                                    Join the Magical Smoke & Vape team! Fill out the application below to be considered for a position at one of our locations.
                                </p>
                            </div>

                            <div className="apply-form">
                                <Form onSubmit={this.handleSubmit}>
                                    {/* Personal Information Section */}
                                    <h3 className="section-title">Personal Information</h3>
                                    
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="fullName" className="form-label">Full Name *</Label>
                                                <Input 
                                                    type="text" 
                                                    id="fullName"
                                                    name="fullName" 
                                                    className="form-input"
                                                    value={this.state.fullName}
                                                    onChange={this.handleInputChange}
                                                    invalid={!!validationErrors.fullName}
                                                    placeholder="Enter your full name"
                                                />
                                                {validationErrors.fullName && <div className="text-danger">{validationErrors.fullName}</div>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="dateOfBirth" className="form-label">Date of Birth *</Label>
                                                <Input 
                                                    type="date" 
                                                    id="dateOfBirth"
                                                    name="dateOfBirth" 
                                                    className="form-input"
                                                    value={this.state.dateOfBirth || ''}
                                                    onChange={this.handleInputChange}
                                                    invalid={!!validationErrors.dateOfBirth}
                                                    placeholder=""
                                                    min="1975-01-01"
                                                    max={this.getTodaysDate()}
                                                />
                                                {validationErrors.dateOfBirth && <div className="text-danger">{validationErrors.dateOfBirth}</div>}
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <FormGroup>
                                        <Label for="street" className="form-label">Street Address *</Label>
                                        <Input 
                                            type="text" 
                                            id="street"
                                            name="street" 
                                            className="form-input"
                                            placeholder="123 Main Street"
                                            value={this.state.street}
                                            onChange={this.handleInputChange}
                                            invalid={!!validationErrors.street}
                                        />
                                        {validationErrors.street && <div className="text-danger">{validationErrors.street}</div>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="apartment" className="form-label">Apartment/Unit (Optional)</Label>
                                        <Input 
                                            type="text" 
                                            id="apartment"
                                            name="apartment" 
                                            className="form-input"
                                            placeholder="Apt 4B, Suite 200, etc."
                                            value={this.state.apartment}
                                            onChange={this.handleInputChange}
                                        />
                                    </FormGroup>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="city" className="form-label">City *</Label>
                                                <Input 
                                                    type="text" 
                                                    id="city"
                                                    name="city" 
                                                    className="form-input"
                                                    value={this.state.city}
                                                    onChange={this.handleInputChange}
                                                    invalid={!!validationErrors.city}
                                                    placeholder="Enter your city"
                                                />
                                                {validationErrors.city && <div className="text-danger">{validationErrors.city}</div>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label for="state" className="form-label">State *</Label>
                                                <Input 
                                                    type="text" 
                                                    id="state"
                                                    name="state" 
                                                    className="form-input"
                                                    placeholder="TN"
                                                    maxLength="2"
                                                    value={this.state.state}
                                                    onChange={this.handleInputChange}
                                                    invalid={!!validationErrors.state}
                                                />
                                                {validationErrors.state && <div className="text-danger">{validationErrors.state}</div>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label for="zipCode" className="form-label">Zip Code *</Label>
                                                <Input 
                                                    type="text" 
                                                    id="zipCode"
                                                    name="zipCode" 
                                                    className="form-input"
                                                    placeholder="12345 or 12345-6789"
                                                    maxLength="10"
                                                    value={this.state.zipCode}
                                                    onChange={(e) => this.handleZipCodeInput(e, 'zipCode')}
                                                    invalid={!!validationErrors.zipCode}
                                                />
                                                {validationErrors.zipCode && <div className="text-danger">{validationErrors.zipCode}</div>}
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="phone" className="form-label">Phone Number *</Label>
                                                <Input 
                                                    type="tel" 
                                                    id="phone"
                                                    name="phone" 
                                                    className="form-input"
                                                    value={this.state.phone}
                                                    onChange={(e) => handlePhoneInput(e, this.setState.bind(this), 'phone')}
                                                    maxLength={14}
                                                    placeholder="(555) 123-4567"
                                                    invalid={!!validationErrors.phone}
                                                />
                                                {validationErrors.phone && <div className="text-danger">{validationErrors.phone}</div>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="email" className="form-label">Email Address *</Label>
                                                <Input 
                                                    type="email" 
                                                    id="email"
                                                    name="email" 
                                                    className="form-input"
                                                    value={this.state.email}
                                                    onChange={this.handleInputChange}
                                                    invalid={!!validationErrors.email}
                                                    placeholder="Enter your email address"
                                                />
                                                {validationErrors.email && <div className="text-danger">{validationErrors.email}</div>}
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    {/* Employment Preferences Section */}
                                    <h3 className="section-title">Employment Preferences</h3>
                                    
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="desiredEmployment" className="form-label">Desired Employment Type *</Label>
                                                <Input 
                                                    type="select" 
                                                    id="desiredEmployment"
                                                    name="desiredEmployment" 
                                                    className="form-input"
                                                    value={this.state.desiredEmployment}
                                                    onChange={this.handleInputChange}
                                                    invalid={!!validationErrors.desiredEmployment}
                                                >
                                                    <option value="">Select employment type</option>
                                                    <option value="Full-Time">Full-Time</option>
                                                    <option value="Part-Time">Part-Time</option>
                                                </Input>
                                                {validationErrors.desiredEmployment && <div className="text-danger">{validationErrors.desiredEmployment}</div>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="desiredLocation" className="form-label">Desired Location *</Label>
                                                <Input 
                                                    type="select" 
                                                    id="desiredLocation"
                                                    name="desiredLocation" 
                                                    className="form-input"
                                                    value={this.state.desiredLocation}
                                                    onChange={this.handleInputChange}
                                                    invalid={!!validationErrors.desiredLocation}
                                                >
                                                    <option value="">Select location</option>
                                                    <option value="Nashville">Nashville</option>
                                                    <option value="Franklin">Franklin</option>
                                                    <option value="Smyrna">Smyrna</option>
                                                    <option value="Franklin Rd">Murfreesboro - Franklin Rd</option>
                                                    <option value="Lascassas Pk">Murfreesboro - Lascassas Pk</option>
                                                    <option value="Memorial Blvd">Murfreesboro - Memorial Blvd</option>
                                                </Input>
                                                {validationErrors.desiredLocation && <div className="text-danger">{validationErrors.desiredLocation}</div>}
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="desiredPay" className="form-label">Desired Pay</Label>
                                                <Input 
                                                    type="text" 
                                                    id="desiredPay"
                                                    name="desiredPay" 
                                                    className="form-input"
                                                    placeholder="e.g., $15/hour, Negotiable"
                                                    value={this.state.desiredPay}
                                                    onChange={this.handleInputChange}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="educationLevel" className="form-label">Education Level *</Label>
                                                <Input 
                                                    type="select" 
                                                    id="educationLevel"
                                                    name="educationLevel" 
                                                    className="form-input"
                                                    value={this.state.educationLevel}
                                                    onChange={this.handleInputChange}
                                                    invalid={!!validationErrors.educationLevel}
                                                >
                                                    <option value="">Select education level</option>
                                                    <option value="High School/GED">High School/GED</option>
                                                    <option value="Some College">Some College</option>
                                                    <option value="Associates">Associates</option>
                                                    <option value="Bachelors">Bachelors</option>
                                                    <option value="Graduate">Graduate</option>
                                                </Input>
                                                {validationErrors.educationLevel && <div className="text-danger">{validationErrors.educationLevel}</div>}
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    {/* Legal Information Section */}
                                    <h3 className="section-title">Legal Information</h3>
                                    
                                    <FormGroup>
                                        <Label className="form-label">Are you legally eligible to work in the U.S.? *</Label>
                                        <div>
                                            <FormGroup check inline>
                                                <Input 
                                                    type="radio" 
                                                    id="legallyEligibleYes"
                                                    name="legallyEligibleToWork" 
                                                    value={true}
                                                    checked={this.state.legallyEligibleToWork === true}
                                                    onChange={() => this.setState({legallyEligibleToWork: true})}
                                                />
                                                <Label check for="legallyEligibleYes">Yes</Label>
                                            </FormGroup>
                                            <FormGroup check inline>
                                                <Input 
                                                    type="radio" 
                                                    id="legallyEligibleNo"
                                                    name="legallyEligibleToWork" 
                                                    value={false}
                                                    checked={this.state.legallyEligibleToWork === false}
                                                    onChange={() => this.setState({legallyEligibleToWork: false})}
                                                />
                                                <Label check for="legallyEligibleNo">No</Label>
                                            </FormGroup>
                                        </div>
                                        {validationErrors.legallyEligibleToWork && <div className="text-danger">{validationErrors.legallyEligibleToWork}</div>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label className="form-label">Have you ever been convicted of a felony? *</Label>
                                        <div>
                                            <FormGroup check inline>
                                                <Input 
                                                    type="radio" 
                                                    id="felonyYes"
                                                    name="hasFelonyConviction" 
                                                    value={true}
                                                    checked={this.state.hasFelonyConviction === true}
                                                    onChange={() => this.setState({hasFelonyConviction: true})}
                                                />
                                                <Label check for="felonyYes">Yes</Label>
                                            </FormGroup>
                                            <FormGroup check inline>
                                                <Input 
                                                    type="radio" 
                                                    id="felonyNo"
                                                    name="hasFelonyConviction" 
                                                    value={false}
                                                    checked={this.state.hasFelonyConviction === false}
                                                    onChange={() => this.setState({hasFelonyConviction: false})}
                                                />
                                                <Label check for="felonyNo">No</Label>
                                            </FormGroup>
                                        </div>
                                        {validationErrors.hasFelonyConviction && <div className="text-danger">{validationErrors.hasFelonyConviction}</div>}
                                    </FormGroup>

                                    {this.state.hasFelonyConviction === true && (
                                        <FormGroup>
                                            <Label for="felonyExplanation" className="form-label">Please explain your felony conviction *</Label>
                                            <Input 
                                                type="textarea" 
                                                id="felonyExplanation"
                                                name="felonyExplanation" 
                                                className="form-input"
                                                rows="3"
                                                value={this.state.felonyExplanation}
                                                onChange={this.handleInputChange}
                                                invalid={!!validationErrors.felonyExplanation}
                                                placeholder="Please provide details about your felony conviction"
                                            />
                                            {validationErrors.felonyExplanation && <div className="text-danger">{validationErrors.felonyExplanation}</div>}
                                        </FormGroup>
                                    )}

                                    <FormGroup>
                                        <Label className="form-label">Would you consent to a background check if asked? *</Label>
                                        <div>
                                            <FormGroup check inline>
                                                <Input 
                                                    type="radio" 
                                                    id="backgroundCheckYes"
                                                    name="consentToBackgroundCheck" 
                                                    value={true}
                                                    checked={this.state.consentToBackgroundCheck === true}
                                                    onChange={() => this.setState({consentToBackgroundCheck: true})}
                                                />
                                                <Label check for="backgroundCheckYes">Yes</Label>
                                            </FormGroup>
                                            <FormGroup check inline>
                                                <Input 
                                                    type="radio" 
                                                    id="backgroundCheckNo"
                                                    name="consentToBackgroundCheck" 
                                                    value={false}
                                                    checked={this.state.consentToBackgroundCheck === false}
                                                    onChange={() => this.setState({consentToBackgroundCheck: false})}
                                                />
                                                <Label check for="backgroundCheckNo">No</Label>
                                            </FormGroup>
                                        </div>
                                        {validationErrors.consentToBackgroundCheck && <div className="text-danger">{validationErrors.consentToBackgroundCheck}</div>}
                                    </FormGroup>

                                    {/* Employment History Section */}
                                    <h3 className="section-title">Previous Employment</h3>
                                    <p className="section-subtitle">Please provide information about your two most recent employers (if applicable).</p>
                                    
                                    {/* Employer 1 */}
                                    <h4 className="subsection-title">Previous Employer 1</h4>
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer1Name" className="form-label">Company Name</Label>
                                                <Input 
                                                    type="text" 
                                                    id="employer1Name"
                                                    name="employer1Name" 
                                                    className="form-input"
                                                    value={this.state.employer1Name}
                                                    onChange={this.handleInputChange}
                                                    placeholder="Enter company name"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer1JobTitle" className="form-label">Job Title</Label>
                                                <Input 
                                                    type="text" 
                                                    id="employer1JobTitle"
                                                    name="employer1JobTitle" 
                                                    className="form-input"
                                                    value={this.state.employer1JobTitle}
                                                    onChange={this.handleInputChange}
                                                    placeholder="Enter your job title"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer1Phone" className="form-label">Phone Number</Label>
                                                <Input 
                                                    type="tel" 
                                                    id="employer1Phone"
                                                    name="employer1Phone" 
                                                    className="form-input"
                                                    value={this.state.employer1Phone}
                                                    onChange={(e) => handlePhoneInput(e, this.setState.bind(this), 'employer1Phone')}
                                                    maxLength={14}
                                                    placeholder="(555) 123-4567"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer1Pay" className="form-label">Pay Rate</Label>
                                                <Input 
                                                    type="text" 
                                                    id="employer1Pay"
                                                    name="employer1Pay" 
                                                    className="form-input"
                                                    placeholder="e.g., $15/hour"
                                                    value={this.state.employer1Pay}
                                                    onChange={this.handleInputChange}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <FormGroup>
                                        <Label for="employer1Street" className="form-label">Street Address</Label>
                                        <Input 
                                            type="text" 
                                            id="employer1Street"
                                            name="employer1Street" 
                                            className="form-input"
                                            placeholder="123 Main Street"
                                            value={this.state.employer1Street}
                                            onChange={this.handleInputChange}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="employer1Apartment" className="form-label">Apartment/Unit (Optional)</Label>
                                        <Input 
                                            type="text" 
                                            id="employer1Apartment"
                                            name="employer1Apartment" 
                                            className="form-input"
                                            placeholder="Apt 4B, Suite 200, etc."
                                            value={this.state.employer1Apartment}
                                            onChange={this.handleInputChange}
                                        />
                                    </FormGroup>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer1City" className="form-label">City</Label>
                                                <Input 
                                                    type="text" 
                                                    id="employer1City"
                                                    name="employer1City" 
                                                    className="form-input"
                                                    value={this.state.employer1City}
                                                    onChange={this.handleInputChange}
                                                    placeholder="Enter city"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label for="employer1State" className="form-label">State</Label>
                                                <Input 
                                                    type="text" 
                                                    id="employer1State"
                                                    name="employer1State" 
                                                    className="form-input"
                                                    placeholder="TN"
                                                    maxLength="2"
                                                    value={this.state.employer1State}
                                                    onChange={this.handleInputChange}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label for="employer1ZipCode" className="form-label">Zip Code</Label>
                                                <Input 
                                                    type="text" 
                                                    id="employer1ZipCode"
                                                    name="employer1ZipCode" 
                                                    className="form-input"
                                                    placeholder="12345 or 12345-6789"
                                                    maxLength="10"
                                                    value={this.state.employer1ZipCode}
                                                    onChange={(e) => this.handleZipCodeInput(e, 'employer1ZipCode')}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer1StartDate" className="form-label">Start Date</Label>
                                                <Input 
                                                    type="date" 
                                                    id="employer1StartDate"
                                                    name="employer1StartDate" 
                                                    className="form-input"
                                                    value={this.state.employer1StartDate || ''}
                                                    onChange={this.handleInputChange}
                                                    placeholder=""
                                                    min="1975-01-01"
                                                    max={this.getTodaysDate()}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer1EndDate" className="form-label">End Date</Label>
                                                <Input 
                                                    type="date" 
                                                    id="employer1EndDate"
                                                    name="employer1EndDate" 
                                                    className="form-input"
                                                    value={this.state.employer1EndDate || ''}
                                                    onChange={this.handleInputChange}
                                                    placeholder=""
                                                    min="1975-01-01"
                                                    max={this.getTodaysDate()}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <FormGroup>
                                        <Label for="employer1Responsibilities" className="form-label">Job Responsibilities</Label>
                                        <Input 
                                            type="textarea" 
                                            id="employer1Responsibilities"
                                            name="employer1Responsibilities" 
                                            className="form-input"
                                            rows="3"
                                            value={this.state.employer1Responsibilities}
                                            onChange={this.handleInputChange}
                                            placeholder="Describe your key responsibilities and duties"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="employer1ReasonForLeaving" className="form-label">Reason for Leaving</Label>
                                        <Input 
                                            type="textarea" 
                                            id="employer1ReasonForLeaving"
                                            name="employer1ReasonForLeaving" 
                                            className="form-input"
                                            rows="2"
                                            value={this.state.employer1ReasonForLeaving}
                                            onChange={this.handleInputChange}
                                            placeholder="Enter reason for leaving this position"
                                        />
                                    </FormGroup>

                                    {/* Employer 2 */}
                                    <h4 className="subsection-title">Previous Employer 2</h4>
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer2Name" className="form-label">Company Name</Label>
                                                <Input 
                                                    type="text" 
                                                    id="employer2Name"
                                                    name="employer2Name" 
                                                    className="form-input"
                                                    value={this.state.employer2Name}
                                                    onChange={this.handleInputChange}
                                                    placeholder="Enter company name"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer2JobTitle" className="form-label">Job Title</Label>
                                                <Input 
                                                    type="text" 
                                                    id="employer2JobTitle"
                                                    name="employer2JobTitle" 
                                                    className="form-input"
                                                    value={this.state.employer2JobTitle}
                                                    onChange={this.handleInputChange}
                                                    placeholder="Enter your job title"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer2Phone" className="form-label">Phone Number</Label>
                                                <Input 
                                                    type="tel" 
                                                    id="employer2Phone"
                                                    name="employer2Phone" 
                                                    className="form-input"
                                                    value={this.state.employer2Phone}
                                                    onChange={(e) => handlePhoneInput(e, this.setState.bind(this), 'employer2Phone')}
                                                    maxLength={14}
                                                    placeholder="(555) 123-4567"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer2Pay" className="form-label">Pay Rate</Label>
                                                <Input 
                                                    type="text" 
                                                    id="employer2Pay"
                                                    name="employer2Pay" 
                                                    className="form-input"
                                                    placeholder="e.g., $15/hour"
                                                    value={this.state.employer2Pay}
                                                    onChange={this.handleInputChange}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <FormGroup>
                                        <Label for="employer2Street" className="form-label">Street Address</Label>
                                        <Input 
                                            type="text" 
                                            id="employer2Street"
                                            name="employer2Street" 
                                            className="form-input"
                                            placeholder="123 Main Street"
                                            value={this.state.employer2Street}
                                            onChange={this.handleInputChange}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="employer2Apartment" className="form-label">Apartment/Unit (Optional)</Label>
                                        <Input 
                                            type="text" 
                                            id="employer2Apartment"
                                            name="employer2Apartment" 
                                            className="form-input"
                                            placeholder="Apt 4B, Suite 200, etc."
                                            value={this.state.employer2Apartment}
                                            onChange={this.handleInputChange}
                                        />
                                    </FormGroup>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer2City" className="form-label">City</Label>
                                                <Input 
                                                    type="text" 
                                                    id="employer2City"
                                                    name="employer2City" 
                                                    className="form-input"
                                                    value={this.state.employer2City}
                                                    onChange={this.handleInputChange}
                                                    placeholder="Enter city"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label for="employer2State" className="form-label">State</Label>
                                                <Input 
                                                    type="text" 
                                                    id="employer2State"
                                                    name="employer2State" 
                                                    className="form-input"
                                                    placeholder="TN"
                                                    maxLength="2"
                                                    value={this.state.employer2State}
                                                    onChange={this.handleInputChange}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label for="employer2ZipCode" className="form-label">Zip Code</Label>
                                                <Input 
                                                    type="text" 
                                                    id="employer2ZipCode"
                                                    name="employer2ZipCode" 
                                                    className="form-input"
                                                    placeholder="12345 or 12345-6789"
                                                    maxLength="10"
                                                    value={this.state.employer2ZipCode}
                                                    onChange={(e) => this.handleZipCodeInput(e, 'employer2ZipCode')}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer2StartDate" className="form-label">Start Date</Label>
                                                <Input 
                                                    type="date" 
                                                    id="employer2StartDate"
                                                    name="employer2StartDate" 
                                                    className="form-input"
                                                    value={this.state.employer2StartDate || ''}
                                                    onChange={this.handleInputChange}
                                                    placeholder=""
                                                    min="1975-01-01"
                                                    max={this.getTodaysDate()}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="employer2EndDate" className="form-label">End Date</Label>
                                                <Input 
                                                    type="date" 
                                                    id="employer2EndDate"
                                                    name="employer2EndDate" 
                                                    className="form-input"
                                                    value={this.state.employer2EndDate || ''}
                                                    onChange={this.handleInputChange}
                                                    placeholder=""
                                                    min="1975-01-01"
                                                    max={this.getTodaysDate()}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <FormGroup>
                                        <Label for="employer2Responsibilities" className="form-label">Job Responsibilities</Label>
                                        <Input 
                                            type="textarea" 
                                            id="employer2Responsibilities"
                                            name="employer2Responsibilities" 
                                            className="form-input"
                                            rows="3"
                                            value={this.state.employer2Responsibilities}
                                            onChange={this.handleInputChange}
                                            placeholder="Describe your key responsibilities and duties"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="employer2ReasonForLeaving" className="form-label">Reason for Leaving</Label>
                                        <Input 
                                            type="textarea" 
                                            id="employer2ReasonForLeaving"
                                            name="employer2ReasonForLeaving" 
                                            className="form-input"
                                            rows="2"
                                            value={this.state.employer2ReasonForLeaving}
                                            onChange={this.handleInputChange}
                                            placeholder="Enter reason for leaving this position"
                                        />
                                    </FormGroup>

                                    {/* References Section */}
                                    <h3 className="section-title">References</h3>
                                    <p className="section-subtitle">Please provide two professional references.</p>
                                    
                                    {/* Reference 1 */}
                                    <h4 className="subsection-title">Reference 1</h4>
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="reference1Name" className="form-label">Name</Label>
                                                <Input 
                                                    type="text" 
                                                    id="reference1Name"
                                                    name="reference1Name" 
                                                    className="form-input"
                                                    value={this.state.reference1Name}
                                                    onChange={this.handleInputChange}
                                                    placeholder="Enter reference name"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="reference1Company" className="form-label">Company</Label>
                                                <Input 
                                                    type="text" 
                                                    id="reference1Company"
                                                    name="reference1Company" 
                                                    className="form-input"
                                                    value={this.state.reference1Company}
                                                    onChange={this.handleInputChange}
                                                    placeholder="Enter company name"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="reference1Title" className="form-label">Title</Label>
                                                <Input 
                                                    type="text" 
                                                    id="reference1Title"
                                                    name="reference1Title" 
                                                    className="form-input"
                                                    value={this.state.reference1Title}
                                                    onChange={this.handleInputChange}
                                                    placeholder="Enter job title"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="reference1Phone" className="form-label">Phone Number</Label>
                                                <Input 
                                                    type="tel" 
                                                    id="reference1Phone"
                                                    name="reference1Phone" 
                                                    className="form-input"
                                                    value={this.state.reference1Phone}
                                                    onChange={(e) => handlePhoneInput(e, this.setState.bind(this), 'reference1Phone')}
                                                    maxLength={14}
                                                    placeholder="(555) 123-4567"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <FormGroup>
                                        <Label for="reference1Email" className="form-label">Email Address</Label>
                                        <Input 
                                            type="email" 
                                            id="reference1Email"
                                            name="reference1Email" 
                                            className="form-input"
                                            value={this.state.reference1Email}
                                            onChange={this.handleInputChange}
                                            placeholder="Enter email address"
                                        />
                                    </FormGroup>

                                    {/* Reference 2 */}
                                    <h4 className="subsection-title">Reference 2</h4>
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="reference2Name" className="form-label">Name</Label>
                                                <Input 
                                                    type="text" 
                                                    id="reference2Name"
                                                    name="reference2Name" 
                                                    className="form-input"
                                                    value={this.state.reference2Name}
                                                    onChange={this.handleInputChange}
                                                    placeholder="Enter reference name"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="reference2Company" className="form-label">Company</Label>
                                                <Input 
                                                    type="text" 
                                                    id="reference2Company"
                                                    name="reference2Company" 
                                                    className="form-input"
                                                    value={this.state.reference2Company}
                                                    onChange={this.handleInputChange}
                                                    placeholder="Enter company name"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="reference2Title" className="form-label">Title</Label>
                                                <Input 
                                                    type="text" 
                                                    id="reference2Title"
                                                    name="reference2Title" 
                                                    className="form-input"
                                                    value={this.state.reference2Title}
                                                    onChange={this.handleInputChange}
                                                    placeholder="Enter job title"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="reference2Phone" className="form-label">Phone Number</Label>
                                                <Input 
                                                    type="tel" 
                                                    id="reference2Phone"
                                                    name="reference2Phone" 
                                                    className="form-input"
                                                    value={this.state.reference2Phone}
                                                    onChange={(e) => handlePhoneInput(e, this.setState.bind(this), 'reference2Phone')}
                                                    maxLength={14}
                                                    placeholder="(555) 123-4567"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <FormGroup>
                                        <Label for="reference2Email" className="form-label">Email Address</Label>
                                        <Input 
                                            type="email" 
                                            id="reference2Email"
                                            name="reference2Email" 
                                            className="form-input"
                                            value={this.state.reference2Email}
                                            onChange={this.handleInputChange}
                                            placeholder="Enter email address"
                                        />
                                    </FormGroup>

                                    {/* Certification */}
                                    <FormGroup check className="certification-section">
                                        <Input 
                                            type="checkbox" 
                                            id="certifyTruthfulness"
                                            name="certifyTruthfulness" 
                                            checked={this.state.certifyTruthfulness}
                                            onChange={this.handleInputChange}
                                            invalid={!!validationErrors.certifyTruthfulness}
                                        />
                                        <Label check for="certifyTruthfulness" className="certification-label">
                                            I certify that my answers are true and honest to the best of my knowledge. If 
                                            this application leads to my eventual employment, I understand that any false 
                                            or misleading information in my application or interview may result in my 
                                            termination from employment. *
                                        </Label>
                                        {validationErrors.certifyTruthfulness && <div className="text-danger">{validationErrors.certifyTruthfulness}</div>}
                                    </FormGroup>

                                    {/* Disclaimer */}
                                    <div className="disclaimer-section">
                                        <p className="disclaimer-text">
                                            Applicant understands that this is an equal opportunity employer and is committed 
                                            to excellence through diversity. To ensure that this application is acceptable, please 
                                            complete each section.
                                        </p>
                                    </div>

                                    {/* reCAPTCHA */}
                                    <FormGroup className="text-center">
                                        <ReCAPTCHA
                                            ref={this.recaptchaRef}
                                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                            onChange={this.handleCaptchaChange}
                                            theme="light"
                                            size="normal"
                                            className="recaptcha-widget"
                                        />
                                        {validationErrors.captchaToken && <div className="text-danger">{validationErrors.captchaToken}</div>}
                                    </FormGroup>

                                    {/* Submit button and messages */}
                                    {this.state.submitMessage && (
                                        <Alert color={this.state.submitError ? 'danger' : 'success'}>
                                            {this.state.submitMessage}
                                        </Alert>
                                    )}

                                    <FormGroup className="text-center">
                                        <Button 
                                            type="submit" 
                                            className="submit-button"
                                            disabled={this.state.isSubmitting}
                                            size="lg"
                                        >
                                            {this.state.isSubmitting ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Submitting Application...
                                                </>
                                            ) : (
                                                'Submit Application'
                                            )}
                                        </Button>
                                    </FormGroup>

                                    {/* Alternative contact */}
                                    <div className="contact-info text-center">
                                        <p className="direct-email">
                                            <p className="apply-subtitle">
                                                If you experience any issues with the application form, please download the{' '}
                                                <a href={"/MagicalApplication.pdf"}>application PDF here</a>,{' '}
                                                fill it out, and email it to{' '}
                                                <a href={"mailto:themagicalvapors@gmail.com"}>themagicalvapors@gmail.com</a>.
                                            </p>
                                        </p>
                                    </div>
                                </Form>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }
}
